import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FilterPanel from '../../components/ui/FilterPanel';
import SearchBar from './components/SearchBar';
import ContactCard from './components/ContactCard';
import ContactForm from './components/ContactForm';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CampaignEmailSendWizard from '../../components/campaigns/CampaignEmailSendWizard';
import {
  fetchProspects,
  createProspect,
  updateProspect,
  validateCallProduct,
  fetchProspectFilterMeta,
  addProspectCustomFieldDefinition,
  apiFetch,
} from '../../api/prospects';
import { canManageCrmCampaigns } from '../../utils/crmAccess';
import { campaignsForProspect } from '../../utils/campaignProspects';
import { customFieldsFromProspect } from '../../utils/prospectCustomFields';
import {
  campaignEmailBannerImageUrl,
  fetchCampaigns,
  fetchCampaignPdfUrl,
} from '../../api/campaigns';
import { createCall, endCall } from '../../api/calls';
import {
  campaignEmailTemplateId,
  latestCampaignEmailTemplate,
} from '../../utils/campaignEmailTemplate';
import { useCrmMailQuota } from '../../hooks/useCrmMailQuota';

const ProspectContactDirectory = () => {
  const { canSendMail, quotaExhausted, refetchCompanies } = useCrmMailQuota();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [assignmentFilter, setAssignmentFilter] = useState('');
  const [selectedTags, setSelectedTags] = useState(() => {
    const tags = searchParams.getAll('tags').filter(Boolean);
    return tags;
  });
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [filterMeta, setFilterMeta] = useState({
    tags: [],
    campaigns: [],
    customFieldDefinitions: [],
  });
  const [loadingFilterMeta, setLoadingFilterMeta] = useState(false);
  const [addingCustomFieldDefinition, setAddingCustomFieldDefinition] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalProspects, setTotalProspects] = useState(0);
  const [validateProductCooldown, setValidateProductCooldown] = useState(false);
  const [mailModal, setMailModal] = useState({
    open: false,
    contact: null,
    campaign: null,
    campaignOptions: [],
    templateId: '',
  });
  const [mailError, setMailError] = useState(null);

  useEffect(() => {
    const tags = searchParams.getAll('tags').filter(Boolean);
    if (tags.length > 0) setSelectedTags(tags);
  }, [searchParams]);

  // Charger l'utilisateur courant une seule fois
  useEffect(() => {
    const loadMe = async () => {
      try {
        const res = await apiFetch('/me');
        setCurrentUser(res?.data?.user || null);
      } catch (e) {
        console.error(e);
      }
    };
    loadMe();
  }, []);

  const canManageProspectFieldDefinitions = useMemo(
    () => canManageCrmCampaigns(currentUser),
    [currentUser],
  );

  const loadFilterMeta = useCallback(async () => {
    setLoadingFilterMeta(true);
    try {
      const res = await fetchProspectFilterMeta();
      setFilterMeta({
        tags: res?.data?.tags || [],
        campaigns: res?.data?.campaigns || [],
        customFieldDefinitions: res?.data?.customFieldDefinitions || [],
      });
    } catch (e) {
      console.error(e);
      setFilterMeta({ tags: [], campaigns: [], customFieldDefinitions: [] });
    } finally {
      setLoadingFilterMeta(false);
    }
  }, []);

  const handleAddCustomFieldDefinition = async (label) => {
    setAddingCustomFieldDefinition(true);
    try {
      await addProspectCustomFieldDefinition(label);
      await loadFilterMeta();
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setAddingCustomFieldDefinition(false);
    }
  };

  const loadData = useCallback(
    async (pageToLoad = 1) => {
      setLoading(true);
      setError(null);
      try {
        const [prospectsRes, campaignsRes] = await Promise.allSettled([
          fetchProspects({
            status: selectedStatus !== 'all' ? selectedStatus : undefined,
            search: searchTerm || undefined,
            company: selectedIndustry !== 'all' ? selectedIndustry : undefined,
            tags: selectedTags.length > 0 ? selectedTags : undefined,
            campaignId: selectedCampaignId || undefined,
            assignedTo:
              assignmentFilter === 'mine' && currentUser?._id ? currentUser._id : undefined,
            assignmentState:
              assignmentFilter === 'unassigned' ? 'unassigned' : undefined,
            sortBy: sortBy !== 'recent' ? sortBy : undefined,
            page: pageToLoad,
            limit: pageSize,
          }),
          fetchCampaigns(),
        ]);

        if (prospectsRes.status === 'fulfilled') {
          const prospects = prospectsRes.value?.data?.prospects || [];
          const pagination = prospectsRes.value?.data?.pagination || {};
          setPage(pagination.page || pageToLoad);
          setTotalProspects(pagination.total || prospects.length);
          const mapped = prospects.map((p) => ({
            id: p._id,
            fullName: p.fullName,
            name: p.fullName,
            company: p.company,
            position: p.position,
            phone: p.phone,
            mobilePhone: p.mobilePhone || '',
            email: p.email,
            industry: p.company || 'N/A',
            status: p.status,
            priority: p.priority,
            callbackAt: p.callbackAt || null,
            lastContact: p.callbackAt
              ? `Rappel le ${new Date(p.callbackAt).toLocaleDateString('fr-FR')}`
              : 'Aucun rappel programmé',
            notes: p.notes,
            address: p.address || '',
            postalCode: p.postalCode || '',
            city: p.city || '',
            website: p.website || '',
            ape: p.ape || '',
            siret: p.siret || '',
            latitude: typeof p.latitude === 'number' ? p.latitude : null,
            longitude: typeof p.longitude === 'number' ? p.longitude : null,
            createdAt: p.createdAt || null,
            avatar:
              'https://img.rocket.new/generatedImages/rocket_gen_img_1e8c7fb95-1763297290338.png',
            avatarAlt: `Avatar de ${p.fullName}`,
            claimedByEmail: p.assignedTo?.email || null,
            tags: Array.isArray(p.tags)
              ? p.tags.map((t) => t?.name).filter(Boolean)
              : [],
            enrichedAt: p.enrichedAt || null,
            customFields: customFieldsFromProspect(p),
          }));
          setContacts(mapped);
        } else {
          setError("Impossible de charger les prospects.");
        }

        if (campaignsRes.status === 'fulfilled') {
          const rawCampaigns = (campaignsRes.value?.data?.campaigns || []).filter(
            (c) => c.status !== 'draft',
          );
          const mappedCampaigns = rawCampaigns.map((c) => ({
            id: c._id,
            companyId: c.companyId?._id || c.companyId || '',
            name: c.name,
            stancerKeysConfigured: Boolean(c.stancerKeysConfigured),
            tags: Array.isArray(c.tags)
              ? c.tags.map((t) => (t && t.name ? t.name : '')).filter(Boolean)
              : [],
            products: Array.isArray(c.products)
              ? c.products.map((p) =>
                  typeof p === 'object'
                    ? {
                        id: p._id,
                        companyId:
                          p.companyId?._id ||
                          p.companyId ||
                          c.companyId?._id ||
                          c.companyId ||
                          '',
                        name: p.name,
                        description: p.description || '',
                        price: typeof p.price === 'number' ? p.price : null,
                        imageUrl: p.imageUrl || '',
                        imageKey: p.imageKey || '',
                        updatedAt: p.updatedAt || '',
                      }
                    : { id: p, name: '', description: '', price: null },
                )
              : [],
            emailTemplateSubject: c.emailTemplateSubject || '',
            emailTemplateHtml: c.emailTemplateHtml || '',
            emailTemplateBannerUrl: c.emailTemplateBannerUrl || '',
            emailTemplateBannerKey: c.emailTemplateBannerKey || '',
            emailTemplateBannerOriginalName:
              c.emailTemplateBannerOriginalName || '',
            emailTemplates: Array.isArray(c.emailTemplates) ? c.emailTemplates : [],
            updatedAt: c.updatedAt || '',
            hubupEmailNotificationMessageId: c.hubupEmailNotificationMessageId || '',
            hubupEmailNotificationTitle: c.hubupEmailNotificationTitle || '',
            hubupEmailNotificationFormId: c.hubupEmailNotificationFormId || '',
            hubupEmailNotificationButtonTarget: c.hubupEmailNotificationButtonTarget || '',
            hubupEmailNotificationButtonLabel: c.hubupEmailNotificationButtonLabel || '',
            hubupEmailNotificationButtonColor: c.hubupEmailNotificationButtonColor || '',
            hubupEmailNotificationButtonBorderColor:
              c.hubupEmailNotificationButtonBorderColor || '',
            hubupEmailNotificationButtonTextColor:
              c.hubupEmailNotificationButtonTextColor || '',
            hubupEmailNotificationButtonShape: c.hubupEmailNotificationButtonShape || '',
            hubupNotificationHistory: Array.isArray(c.hubupNotificationHistory)
              ? c.hubupNotificationHistory
              : [],
          }));
          setCampaigns(mappedCampaigns);
        }
      } catch (e) {
        console.error(e);
        setError("Impossible de charger les prospects.");
      } finally {
        setLoading(false);
      }
    },
    [
      selectedStatus,
      searchTerm,
      selectedIndustry,
      selectedTags,
      selectedCampaignId,
      assignmentFilter,
      currentUser,
      sortBy,
      pageSize,
    ],
  );

  useEffect(() => {
    loadData(1);
  }, [loadData]);

  useEffect(() => {
    void loadFilterMeta();
  }, [loadFilterMeta]);

  const selectedCampaignMeta = useMemo(
    () =>
      (filterMeta.campaigns || []).find((c) => c.id === selectedCampaignId) ||
      null,
    [filterMeta.campaigns, selectedCampaignId],
  );

  const handleAddContact = async (formData) => {
    try {
      const payload = {
        fullName: formData.fullName,
        company: formData.company,
        position: formData.position,
        phone: formData.phone,
        mobilePhone: formData.mobilePhone || '',
        email: formData.email,
        status: formData.status,
        priority: formData.priority,
        notes: formData.notes,
        callbackAt: formData.callbackAt
          ? new Date(formData.callbackAt).toISOString()
          : null,
        address: formData.address || '',
        postalCode: formData.postalCode || '',
        city: formData.city || '',
        website: formData.website || '',
        ape: formData.ape || '',
        siret: formData.siret || '',
        latitude: formData.latitude ? Number(formData.latitude) : undefined,
        longitude: formData.longitude ? Number(formData.longitude) : undefined,
        tags: formData.tags || [],
        customFields: formData.customFields || [],
      };

      const res = await createProspect(payload);
      const p = res?.data?.prospect;
      if (p) {
        const newContact = {
          id: p._id,
          fullName: p.fullName,
          name: p.fullName,
          company: p.company,
          position: p.position,
          phone: p.phone,
          mobilePhone: p.mobilePhone || '',
          email: p.email,
          industry: p.company || 'N/A',
          status: p.status,
          priority: p.priority,
          lastContact: p.callbackAt
            ? `Rappel le ${new Date(p.callbackAt).toLocaleDateString('fr-FR')}`
            : 'Aucun rappel programmé',
          notes: p.notes,
          address: p.address || '',
          postalCode: p.postalCode || '',
          city: p.city || '',
          website: p.website || '',
          ape: p.ape || '',
          siret: p.siret || '',
          latitude: typeof p.latitude === 'number' ? p.latitude : null,
          longitude: typeof p.longitude === 'number' ? p.longitude : null,
          createdAt: p.createdAt || null,
          avatar:
            'https://img.rocket.new/generatedImages/rocket_gen_img_1e8c7fb95-1763297290338.png',
          avatarAlt: `Avatar de ${p.fullName}`,
          claimedByEmail: p.assignedTo?.email || null,
          tags: Array.isArray(p.tags)
            ? p.tags.map((t) => t?.name).filter(Boolean)
            : [],
          customFields: customFieldsFromProspect(p),
        };
        setContacts((prev) => [newContact, ...prev]);
      }
      setShowAddForm(false);
      void loadFilterMeta();
    } catch (e) {
      console.error(e);
    }
  };

  const handleClaim = async (id) => {
    if (!currentUser) return;
    try {
      await updateProspect(id, { assignedTo: currentUser._id });
      setContacts((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, claimedByEmail: currentUser.email } : c,
        ),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleProspectEnriched = (id, p) => {
    setContacts((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              fullName: p.fullName,
              name: p.fullName,
              company: p.company,
              position: p.position,
              phone: p.phone,
              mobilePhone: p.mobilePhone || '',
              email: p.email,
              address: p.address || '',
              postalCode: p.postalCode || '',
              city: p.city || '',
              website: p.website || '',
              enrichedAt: p.enrichedAt || c.enrichedAt || null,
            }
          : c,
      ),
    );
  };

  const handleUpdateContact = async (id, updates) => {
    try {
      const res = await updateProspect(id, updates);
      const p = res?.data?.prospect;
      setContacts((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...c,
                fullName: p.fullName,
                name: p.fullName,
                company: p.company,
                position: p.position,
                phone: p.phone,
                mobilePhone: p.mobilePhone || '',
                email: p.email,
                status: p.status,
                priority: p.priority,
                notes: p.notes,
                callbackAt: p.callbackAt || null,
                lastContact: p.callbackAt
                  ? `Rappel le ${new Date(p.callbackAt).toLocaleDateString('fr-FR')}`
                  : 'Aucun rappel programmé',
                address: p.address || '',
                postalCode: p.postalCode || '',
                city: p.city || '',
                website: p.website || '',
                ape: p.ape || '',
                siret: p.siret || '',
                latitude: typeof p.latitude === 'number' ? p.latitude : null,
                longitude: typeof p.longitude === 'number' ? p.longitude : null,
                tags: Array.isArray(p.tags)
                  ? p.tags.map((t) => t?.name).filter(Boolean)
                  : [],
                customFields: customFieldsFromProspect(p),
              }
            : c,
        ),
      );
      setEditingContact(null);
      void loadFilterMeta();
    } catch (e) {
      console.error(e);
    }
  };

  const handleStartCall = async (contact) => {
    try {
      // Lance l'appel natif
      if (contact?.phone) {
        window.location.href = `tel:${contact.phone}`;
      }

      // Crée le call en base uniquement pour les non-admins
      let call = null;
      if (!currentUser || currentUser.permission !== 'admin') {
        const res = await createCall({
          prospectId: contact.id,
          motif: 'prospect',
          status: 'success',
        });
        call = res?.data?.call || null;
      }

      // Campagnes dont les tags intersectent ceux du prospect
      const prospectTags = Array.isArray(contact.tags) ? contact.tags : [];
      let campaignMatches = campaigns
        .map((c) => {
          const matchedTags = c.tags.filter((t) => prospectTags.includes(t));
          const hasProducts = Array.isArray(c.products) && c.products.length > 0;
          if (matchedTags.length === 0 || !hasProducts) return null;
          return {
            id: c.id,
            name: c.name,
            stancerKeysConfigured: Boolean(c.stancerKeysConfigured),
            matchedTags,
            products: c.products,
          };
        })
        .filter(Boolean);

      // Si aucune campagne ne matche par tags, on prend toutes les campagnes qui ont des produits
      if (!campaignMatches.length) {
        campaignMatches = campaigns
          .filter((c) => Array.isArray(c.products) && c.products.length > 0)
          .map((c) => ({
            id: c.id,
            name: c.name,
            stancerKeysConfigured: Boolean(c.stancerKeysConfigured),
            matchedTags: [],
            products: c.products,
          }));
      }

      // Produit par défaut (premier produit de la première campagne qui matche)
      let defaultProductId = '';
      let defaultCampaignId = '';
      let defaultAmount = '';
      if (campaignMatches.length > 0 && campaignMatches[0].products.length > 0) {
        const p = campaignMatches[0].products[0];
        defaultProductId = p.id;
        defaultCampaignId = campaignMatches[0].id;
        if (typeof p.price === 'number') {
          defaultAmount = String(p.price);
        }
      }

      let pdfUrl = '';
      if (campaignMatches.length > 0) {
        try {
          const pdfRes = await fetchCampaignPdfUrl(campaignMatches[0].id);
          pdfUrl = pdfRes?.data?.url || '';
        } catch (err) {
          console.error(err);
        }
      }

      setActiveCall({
        contact,
        callId: call?._id,
        callStatus: 'success',
        prospectStatus: contact.status,
        // Ne pas pré-remplir le rappel pour permettre de le reset facilement
        callbackAt: '',
        cr: '',
        amount: defaultAmount,
        selectedProductId: defaultProductId,
        selectedCampaignId: defaultCampaignId,
        campaignMatches,
        email: contact.email || '',
        pdfUrl: pdfUrl || '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenMailModal = (contact) => {
    if (!contact || !Array.isArray(campaigns)) {
      return;
    }

    if (!canSendMail) {
      window.dispatchEvent(new CustomEvent('crm:open-mail-pack-offers'));
      return;
    }

    if (!String(contact.email || '').trim()) {
      setMailError("Ce prospect n'a pas d'adresse email.");
      return;
    }

    const prospectTags = Array.isArray(contact.tags) ? contact.tags : [];

    let campaignMatches = campaigns
      .map((c) => {
        const matchedTags = c.tags.filter((t) => prospectTags.includes(t));
        return {
          ...c,
          matchedTags,
        };
      })
      .filter((c) => c.matchedTags.length > 0);

    if (!campaignMatches.length) {
      // fallback : toute campagne, meme sans template, pour pouvoir partir de zero
      campaignMatches = campaigns.map((c) => ({
        ...c,
        matchedTags: [],
      }));
    }

    if (!campaignMatches.length) {
      setMailError("Aucune campagne n'est disponible pour préparer un email.");
      return;
    }

    const firstCampaign = campaignMatches[0];
    const firstSavedTemplate = latestCampaignEmailTemplate(firstCampaign);

    setMailModal({
      open: true,
      contact,
      campaign: firstCampaign,
      campaignOptions: campaignMatches,
      templateId: campaignEmailTemplateId(firstSavedTemplate),
    });
    setMailError(null);
  };

  const handleCloseCall = async () => {
    if (!activeCall) return;
    try {
      const {
        contact,
        callId,
        callStatus,
        prospectStatus,
        callbackAt,
        cr,
        amount,
        email,
      } = activeCall;

      // Mise à jour du prospect
      const resProspect = await updateProspect(contact.id, {
        status: prospectStatus,
        callbackAt: callbackAt ? new Date(callbackAt).toISOString() : null,
        email: email || contact.email,
      });
      const p = resProspect?.data?.prospect;

      setContacts((prev) =>
        prev.map((c) =>
          c.id === contact.id
            ? {
                ...c,
                email: p.email,
                status: p.status,
                priority: p.priority,
                notes: p.notes,
                callbackAt: p.callbackAt || null,
                lastContact: p.callbackAt
                  ? `Rappel le ${new Date(p.callbackAt).toLocaleDateString('fr-FR')}`
                  : 'Aucun rappel programmé',
              }
            : c,
        ),
      );

      // Fin de l'appel
      if (callId) {
        await endCall(callId, {
          status: callStatus,
          cr,
          amount: amount ? Number(amount) : undefined,
        });
      }

      setActiveCall(null);
    } catch (e) {
      console.error(e);
    }
  };

  const campaignProductOptions =
    activeCall && Array.isArray(activeCall.campaignMatches)
      ? activeCall.campaignMatches.flatMap((cm) =>
          (cm.products || []).map((p) => ({
            id: p.id,
            optionId: `${cm.id}:${p.id}`,
            campaignId: cm.id,
            name: p.name,
            campaignName: cm.name,
            price: p.price,
            stancerKeysConfigured: Boolean(cm.stancerKeysConfigured),
          })),
        )
      : [];
  const selectedCampaignProductOption = activeCall
    ? campaignProductOptions.find(
        (p) =>
          p.id === activeCall.selectedProductId &&
          p.campaignId === activeCall.selectedCampaignId,
      ) || null
    : null;
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Annuaire - CRM</title>
      </Helmet>
      <Header />
      <Breadcrumb />
      <FilterPanel />

      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Annuaire
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Gérez vos contacts et composez directement depuis l&apos;interface
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/prospect-contact-directory/search')}
                iconName="Search"
                iconPosition="left"
                size="lg"
              >
                Rechercher des prospects
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/prospect-contact-directory/import')}
                iconName="Upload"
                iconPosition="left"
                size="lg"
              >
                Importer CSV
              </Button>
              <Button
                onClick={() => setShowAddForm(true)}
                iconName="UserPlus"
                iconPosition="left"
                size="lg"
              >
                Ajouter un prospect
              </Button>
            </div>
          </div>

          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedIndustry={selectedIndustry}
            onIndustryChange={setSelectedIndustry}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            assignmentFilter={assignmentFilter}
            onAssignmentFilterChange={setAssignmentFilter}
            selectedTags={selectedTags}
            onSelectedTagsChange={setSelectedTags}
            selectedCampaignId={selectedCampaignId}
            onSelectedCampaignChange={setSelectedCampaignId}
            filterMeta={filterMeta}
            loadingFilterMeta={loadingFilterMeta}
          />
        </div>

        {selectedCampaignMeta ? (
          <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
            <p className="font-medium text-foreground">
              Vue campagne « {selectedCampaignMeta.name} »
            </p>
            <p className="text-muted-foreground text-xs mt-1">
              {selectedCampaignMeta.tags?.length > 0 ? (
                <>
                  Prospects affichés : possèdent{' '}
                  <span className="font-medium text-foreground">tous</span> les tags{' '}
                  {selectedCampaignMeta.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex mx-0.5 px-1.5 py-0.5 rounded bg-background border border-border text-[11px]"
                    >
                      {t}
                    </span>
                  ))}{' '}
                  ({selectedCampaignMeta.count} au total).
                </>
              ) : (
                <>Cette campagne n&apos;a aucun tag : aucun prospect ne peut y être rattaché.</>
              )}
            </p>
          </div>
        ) : null}

        <div className="mb-4">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">
              {totalProspects}
            </span>{' '}
            prospect(s) trouvé(s)
          </p>
          <p className="text-xs text-muted-foreground">
            Page {page} – {totalProspects} prospect(s) au total
          </p>
          {loading && (
            <p className="text-xs text-muted-foreground mt-1">
              Chargement des prospects...
            </p>
          )}
          {error && (
            <p className="text-xs text-error mt-1">
              {error}
            </p>
          )}
          {mailError && !mailModal.open && (
            <p className="text-xs text-error mt-1">
              {mailError}
            </p>
          )}
        </div>

        {!loading && contacts.length === 0 ? (
          <div className="bg-card rounded-lg p-12 text-center">
            <Icon
              name="Users"
              size={48}
              className="mx-auto mb-4 text-muted-foreground"
            />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Aucun prospect trouvé
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Essayez de modifier vos critères de recherche ou ajoutez un nouveau
              prospect
            </p>
            <Button
              onClick={() => setShowAddForm(true)}
              iconName="UserPlus"
              iconPosition="left"
            >
              Ajouter un prospect
            </Button>
          </div>
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6'
                : 'space-y-4'
            }
          >
            {contacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                campaigns={campaigns}
                viewMode={viewMode}
                onClaim={handleClaim}
                onEdit={() => setEditingContact(contact)}
                onCall={handleStartCall}
                onMail={handleOpenMailModal}
                onEnriched={handleProspectEnriched}
                mailDisabled={!canSendMail}
              />
            ))}
          </div>
        )}

        {totalProspects > pageSize && (
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => loadData(page - 1)}
            >
              Précédent
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} / {Math.max(1, Math.ceil(totalProspects / pageSize))}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= Math.max(1, Math.ceil(totalProspects / pageSize))}
              onClick={() => loadData(page + 1)}
            >
              Suivant
            </Button>
          </div>
        )}
      </main>

      {showAddForm && (
        <ContactForm
          campaigns={campaigns}
          customFieldDefinitions={filterMeta.customFieldDefinitions}
          canManageDefinitions={canManageProspectFieldDefinitions}
          onAddCustomFieldDefinition={handleAddCustomFieldDefinition}
          addingCustomFieldDefinition={addingCustomFieldDefinition}
          onClose={() => setShowAddForm(false)}
          onSubmit={handleAddContact}
        />
      )}
      {editingContact && (
        <ContactForm
          campaigns={campaigns}
          customFieldDefinitions={filterMeta.customFieldDefinitions}
          canManageDefinitions={canManageProspectFieldDefinitions}
          onAddCustomFieldDefinition={handleAddCustomFieldDefinition}
          addingCustomFieldDefinition={addingCustomFieldDefinition}
          onClose={() => setEditingContact(null)}
          onSubmit={(data) =>
            handleUpdateContact(editingContact.id, {
              fullName: data.fullName,
              company: data.company,
              position: data.position,
              phone: data.phone,
              mobilePhone: data.mobilePhone || '',
              email: data.email,
              status: data.status,
              priority: data.priority,
              notes: data.notes,
              address: data.address || '',
              postalCode: data.postalCode || '',
              city: data.city || '',
              website: data.website || '',
              ape: data.ape || '',
              siret: data.siret || '',
              latitude: data.latitude ? Number(data.latitude) : undefined,
              longitude: data.longitude ? Number(data.longitude) : undefined,
              tags: data.tags || [],
              callbackAt: data.callbackAt
                ? new Date(data.callbackAt).toISOString()
                : null,
              customFields: data.customFields || [],
            })
          }
          initialData={editingContact}
        />
      )}
      {activeCall && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1100] p-2 md:p-4">
          <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-7xl h-[92vh] overflow-hidden">
            <div
              className={
                activeCall.pdfUrl
                  ? 'p-4 h-full grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-4'
                  : 'p-4 h-full space-y-4'
              }
            >
              <div
                className={
                  activeCall.pdfUrl
                    ? 'space-y-4 flex flex-col h-full overflow-y-auto'
                    : 'space-y-4 text-sm'
                }
              >
                <div className="mb-3">
                  <h2 className="text-lg font-semibold text-foreground">
                    Appel avec {activeCall.contact.name}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {activeCall.contact.company} • {activeCall.contact.position}
                  </p>
                </div>
                <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <Icon name="Phone" size={14} className="text-muted-foreground" />
                  <span>{activeCall.contact.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={14} className="text-muted-foreground" />
                  <span>{activeCall.email || activeCall.contact.email}</span>
                </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1 block">
                    Statut de l&apos;appel
                  </label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={activeCall.callStatus}
                    onChange={(e) =>
                      setActiveCall((prev) => ({ ...prev, callStatus: e.target.value }))
                    }
                  >
                    <option value="success">Succès</option>
                    <option value="no-answer">Pas de réponse</option>
                    <option value="callback">Rappel requis</option>
                    <option value="voicemail">Messagerie</option>
                    <option value="not-interested">Pas intéressé</option>
                    <option value="wrong-number">Mauvais numéro</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground mb-1 block">
                    Statut du prospect
                  </label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={activeCall.prospectStatus}
                    onChange={(e) =>
                      setActiveCall((prev) => ({
                        ...prev,
                        prospectStatus: e.target.value,
                      }))
                    }
                  >
                    <option value="prospect">Prospect</option>
                    <option value="qualifie">Qualifié</option>
                    <option value="proposition">Proposition</option>
                    <option value="negociation">Négociation</option>
                    <option value="conclu">Conclu</option>
                    <option value="non_interesse">Non intéressé</option>
                  </select>
                </div>
                </div>

                <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Rappel (optionnel)
                </label>
                <input
                  type="datetime-local"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={activeCall.callbackAt}
                  onChange={(e) =>
                    setActiveCall((prev) => ({ ...prev, callbackAt: e.target.value }))
                  }
                />
                </div>

                <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Email du prospect
                </label>
                <input
                  type="email"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={activeCall.email || ''}
                  onChange={(e) =>
                    setActiveCall((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
                </div>

                <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Produit de campagne associé
                </label>
                {campaignProductOptions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Aucun produit de campagne trouvé pour les tags de ce prospect.
                  </p>
                ) : (
                  <>
                    <select
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      value={
                        activeCall.selectedCampaignId && activeCall.selectedProductId
                          ? `${activeCall.selectedCampaignId}:${activeCall.selectedProductId}`
                          : ''
                      }
                      onChange={(e) => {
                        const [selectedCampaignId, selectedId] = e.target.value.split(':');
                        const selected = campaignProductOptions.find(
                          (p) =>
                            p.id === selectedId &&
                            p.campaignId === selectedCampaignId,
                        );
                        setActiveCall((prev) => ({
                          ...prev,
                          selectedProductId: selectedId,
                          selectedCampaignId,
                          amount:
                            selected && typeof selected.price === 'number'
                              ? String(selected.price)
                              : '',
                        }));
                      }}
                    >
                      {campaignProductOptions.map((p) => (
                        <option key={p.optionId} value={p.optionId}>
                          {p.name || 'Produit'} ({p.campaignName})
                          {typeof p.price === 'number' ? ` - ${p.price} €` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center gap-2 mt-2 text-sm">
                      <span className="text-muted-foreground">Montant sélectionné :</span>
                      <span className="font-medium text-foreground">
                        {activeCall.amount ? `${activeCall.amount} €` : '—'}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        iconName="Check"
                        disabled={
                          validateProductCooldown ||
                          !selectedCampaignProductOption?.stancerKeysConfigured
                        }
                        onClick={async () => {
                          const selected = selectedCampaignProductOption;
                          const price =
                            selected && typeof selected.price === 'number'
                              ? String(selected.price)
                              : '';
                          const email = activeCall.email || activeCall.contact.email || '';
                          try {
                            await validateCallProduct(activeCall.contact.id, {
                              campaignId: activeCall.selectedCampaignId,
                              productId: activeCall.selectedProductId,
                              email,
                            });
                          } catch (e) {
                            console.error(e);
                            return;
                          }
                          setActiveCall((prev) => ({
                            ...prev,
                            amount: price,
                          }));
                          setValidateProductCooldown(true);
                          setTimeout(() => setValidateProductCooldown(false), 5000);
                        }}
                      >
                        {validateProductCooldown ? 'Validé (5s)' : 'Valider'}
                      </Button>
                    </div>
                    {!selectedCampaignProductOption?.stancerKeysConfigured ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Paiement auto indisponible —{' '}
                        <a href="/crm-configuration" className="text-primary underline">
                          configurer Stancer
                        </a>
                      </p>
                    ) : null}
                  </>
                )}
                </div>

                <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Compte rendu (CR)
                </label>
                <textarea
                  rows={4}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Notez ici le compte rendu de l'appel..."
                  value={activeCall.cr}
                  onChange={(e) =>
                    setActiveCall((prev) => ({ ...prev, cr: e.target.value }))
                  }
                />
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    variant="success"
                    iconName="PhoneOff"
                    iconPosition="left"
                    onClick={handleCloseCall}
                  >
                    Clôturer l&apos;appel
                  </Button>
                </div>
              </div>
              {activeCall.pdfUrl && (
                <div className="border-t md:border-t-0 md:border-l border-border md:pl-3 pt-2 md:pt-0 flex flex-col h-full">
                  <div className="flex-1 rounded-md border border-border overflow-hidden bg-muted">
                    <object
                      data={activeCall.pdfUrl}
                      type="application/pdf"
                      className="w-full h-full"
                    >
                      <div className="h-full flex flex-col items-center justify-center gap-3 p-4 text-center">
                        <p className="text-xs text-muted-foreground">
                          Impossible d&apos;afficher le PDF directement dans votre navigateur.
                        </p>
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                        >
                          <a href={activeCall.pdfUrl} target="_blank" rel="noopener noreferrer">
                            Ouvrir le PDF dans un nouvel onglet
                          </a>
                        </Button>
                      </div>
                    </object>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {mailModal.open && mailModal.contact && mailModal.campaign ? (
        <CampaignEmailSendWizard
          mode="prospect"
          campaign={mailModal.campaign}
          campaigns={
            mailModal.campaignOptions?.length > 1 ? mailModal.campaignOptions : null
          }
          contact={mailModal.contact}
          initialTemplateId={mailModal.templateId || ''}
          onClose={() =>
            setMailModal({
              open: false,
              contact: null,
              campaign: null,
              campaignOptions: [],
              templateId: '',
            })
          }
          onSuccess={() => refetchCompanies?.()}
        />
      ) : null}
    </div>
  );
};

export default ProspectContactDirectory;
